if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.sliderInstance = false;
        this.thumbsInstance = false;
        this._visibleSlideIds = null;
      }

      connectedCallback() {
        FoxTheme.Motion.inView(this, this.init.bind(this));
      }

      init() {
        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
          this.initImageZoom(allMedia);
        }
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);

        this.sliderOptions = {
          init: false,
          slidesPerView: '1',
          spaceBetween: mediaItemGap,
          loop: false,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
            },
          },
          spaceBetween: mediaItemGap,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions = Object.assign({}, this.thumbsOptions, {
              breakpoints: {
                768: {
                  direction: 'vertical',
                  slidesPerView: 'auto',
                },
              },
            });
            break;
          case 'slider-freemode':
            this.sliderOptions = Object.assign({}, this.sliderOptions, {
              slidesPerView: 'auto',
            });
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            // Thumbs sync handled manually — do not use Swiper built-in thumbs
            // this.sliderOptions.thumbs = {
            //   swiper: this.thumbsInstance.slider,
            //   autoScrollOffset: 2,
            // };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
          this.initThumbClicks();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initThumbClicks() {
        if (!this.elements.thumbnails) return;
        this.elements.thumbnails.addEventListener('click', (e) => {
          const thumb = e.target.closest('.product__thumbs-item');
          if (!thumb) return;
          const mediaId = thumb.dataset.target;
          if (!mediaId) return;

          const allSlides = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
          const targetIndex = allSlides.findIndex(s => s.dataset.mediaId === mediaId);

          if (targetIndex >= 0 && this.sliderInstance && this.sliderInstance.slider) {
            this.sliderInstance.slider.slideTo(targetIndex, 300, false);
          }

          this._highlightThumb(mediaId);
        });
      }

      initImageZoom(items = []) {
        let dataSource = [];
        if (items.length > 0) {
          items.forEach((media) => {
            switch (media.dataset.mediaType) {
              case 'model':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('product-model').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'video':
              case 'external_video':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('video-element').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'image':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  src: media.dataset.src,
                  width: media.dataset.pswpWidth,
                  height: media.dataset.pswpHeight,
                  mediaType: media.dataset.mediaType,
                });
                break;
            }
          });
        }

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
          preload: [1, 4],
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) return el;
            }
          }
          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data }) => {
          if (data.src) return data.src;
          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
        });

        this.lightbox.on('destroy', () => {
          const { currIndex } = this.lightbox.pswp;
          if (this.sliderInstance && this.sliderInstance.slider) {
            this.sliderInstance.slider.slideToLoop(currIndex, 0, false);
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox.pswp.currSlide.data.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => { this.lightbox.pswp.next(); },
            });
            this.lightbox.pswp.ui.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => { this.lightbox.pswp.prev(); },
            });
          }
          this.lightbox.pswp.ui.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => { this.lightbox.pswp.close(); },
          });
        });

        this.lightbox.init();

        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset.mediaType === 'image') {
              const index = Number(media.dataset.mediaIndex) || 0;
              this.lightbox.loadAndOpen(index);
            }
          },
        });
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;
          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
         let lastIndex = 0;
        this.sliderInstance.slider.on('slideChange', (swiper) => {
          const { slides, activeIndex } = swiper;
          const direction =
  activeIndex > lastIndex ? 'next' : 'prev';

lastIndex = activeIndex;

          // If filtering is active, skip hidden slides
        if (this._visibleSlideIds) {
  const currentSlide = slides[activeIndex];

  if (currentSlide && !this._visibleSlideIds.has(currentSlide.dataset.mediaId)) {

    const visibleIndex = this._findNearestVisibleIndex(
      slides,
      activeIndex,
      direction
    );

    if (visibleIndex !== activeIndex) {
      setTimeout(() => swiper.slideTo(visibleIndex, 0, false), 0);
      return;
    }
  }
}

          // Sync thumbnail highlight by media id
        if (this.elements.thumbnails && slides[activeIndex]) {
  const mediaId = slides[activeIndex].dataset.mediaId;

  this._highlightThumb(mediaId);

  if (this._visibleSlides) {
    this._updateNavigationState(mediaId);
  }
}

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

 _findNearestVisibleIndex(slides, fromIndex, direction) {

  if (direction === 'next') {
    for (let i = fromIndex + 1; i < slides.length; i++) {
      if (this._visibleSlideIds.has(slides[i].dataset.mediaId)) {
        return i;
      }
    }
  }

  if (direction === 'prev') {
    for (let i = fromIndex - 1; i >= 0; i--) {
      if (this._visibleSlideIds.has(slides[i].dataset.mediaId)) {
        return i;
      }
    }
  }

  return fromIndex;
}
_updateNavigationState(activeMediaId) {
  if (!this._visibleSlides || !this._visibleSlides.length) return;

  const firstId = this._visibleSlides[0].dataset.mediaId;
  const lastId =
    this._visibleSlides[this._visibleSlides.length - 1].dataset.mediaId;

  const prevBtn = this.querySelector('.swiper-button-prev');
  const nextBtn = this.querySelector('.swiper-button-next');

  if (prevBtn) {
    prevBtn.classList.toggle(
      'swiper-button-disabled',
      String(activeMediaId) === String(firstId)
    );
  }

  if (nextBtn) {
    nextBtn.classList.toggle(
      'swiper-button-disabled',
      String(activeMediaId) === String(lastId)
    );
  }
}

      _highlightThumb(mediaId) {
        if (!this.elements.thumbnails) return;
        const allThumbs = [...this.elements.thumbnails.querySelectorAll('.product__thumbs-item:not(.swiper-slide-duplicate)')];
        const visibleThumbs = allThumbs.filter(t => t.style.display !== 'none');

        allThumbs.forEach(thumb => {
          const isActive = thumb.dataset.target === mediaId;
          thumb.classList.toggle('is-active', isActive);
          thumb.classList.toggle('swiper-slide-thumb-active', isActive);
        });

        if (this.thumbsInstance && this.thumbsInstance.slider) {
          const activeVisibleIndex = visibleThumbs.findIndex(t => t.dataset.target === mediaId);
          if (activeVisibleIndex >= 0) {
            this.thumbsInstance.slider.slideTo(activeVisibleIndex, 300, false);
          }
        }
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      // Treats "ß" and "ss" as equivalent so alt text authored either way still matches
      normalizeGerman(str) {
        return (str || '')
          .toLowerCase()
          .trim()
          .replace(/ß/g, 'ss');
      }

      // Reads the storefront locale so we know whether translation into German is needed
      getCurrentLocale() {
        return (window.Shopify && window.Shopify.locale) || document.documentElement.lang || 'en';
      }

      setActiveMedia(variant) {
        if (!variant) return;

     //ENGLIST TO GERMAN
        const optionMapEN = {
          'red': 'rot',
          'black': 'schwarz',
          'blue': 'blau',
          'green': 'grün',
          'pink': 'rosa',
          'white': 'weiss',
          'yellow': 'gelb',
          'silver': 'silber',
          'double occupancy': '2-fach bestückung',
          'fully populated': 'vollbestückung',
          'gold': 'gold',
          'light pink': 'hellrosa',
          'light blue': 'hellblau',
          'dark blue': 'dunkelblau',
          'dark green': 'dunkelgrün',
          'turquoise': 'türkis',
          'violet': 'violett',
          'purple': 'lila',
          'berry': 'beere',
          'cherry': 'kirsche',
          'beige': 'beige',
          'cognac': 'cognac',
          'decor': 'dekor',
          'fuchsia': 'fuchsia',
          '2 pieces': '2 stück',
          '12 pieces': '12 stück',
          'no. 1': 'nr. 1',
          'no. 1.5': 'nr. 1.5',
          'no. 2': 'nr. 2',
          'no. 3': 'nr. 3',
          'no. 10': 'nr. 10',
          'no. 11': 'nr. 11',
          'no. 12': 'nr. 12',
          'no. 13': 'nr. 13',
          'no. 14': 'nr. 14',
          'no. 15': 'nr. 15',
          'no. 16': 'nr. 16',
          'no. 17': 'nr. 17',
          'no. 18': 'nr. 18',
          'no. 19': 'nr. 19',
          'no. 20': 'nr. 20',
          'no. 21': 'nr. 21',
          'no. 22': 'nr. 22',
          'no. 23': 'nr. 23',
          'no. 24': 'nr. 24',
          'no. 25': 'nr. 25',
          'bevelled on both sides': 'beidseitig schräg',
          'beveled on both sides': 'beidseitig schräg',
          'beveled on one side': 'einseitig schräg',
          'bevelled on one side': 'einseitig schräg',
          'design 18 cm': 'design 18cm',
          'football 18cm': 'fußball 18cm',
          'seasons 10cm': 'jahreszeiten 10cm',
          "mother's day 18cm": 'muttertag 18cm',
          'snowflake 10cm': 'schneeflocke 10cm',
          'stainless': 'rostfrei',
          'nickel-plated': 'vernickelt',
          'plastic': 'kunststoff',
          'hard': 'hart',
          'average': 'mittel',
          'soft': 'weich',
          'wide': 'breit',
          'broad': 'breit',
          'narrow': 'schmal',
          'large': 'groß',
          'small': 'klein',
          'straight': 'gerade',
          'rounded tip': 'gerundete spitze',
          'flat tip': 'flache spitze',
          'slim tip': 'schlanke spitze',
          'flame point': 'flammenspitze',
          'a tip': 'eine spitze',
          'two peaks': 'zwei spitzen',
          'pointed': 'spitz',
          'bent': 'gebogen',
          'cranked': 'gekröpft',
          'quirky': 'schräg',
          'mixed': 'gemischt',
          'assorted (emery boards)': 'gemischt (sandblattfeilen)',
          'assorted (sapphire nail files)': 'gemischt (saphirfeilen)',
          'with display': 'mit display',
          'without display': 'ohne display',
          'without a display': 'ohne display',
          'in the acrylic display': 'im acryl-display',
          'in the acrylic display v2': 'im acryl-display v2',
          'in the wooden display': 'im holz-display',
          'colorful': 'bunt',
          'colorless': 'farblos',
          'flower': 'blume',
          'with crystals': 'mit kristallen',
          'book': 'buche',
          'cell id': 'cellid',
        };

        // ITALIAN TO GERMAN
        const optionMapIT = {
          'rosso': 'rot',
          'nero': 'schwarz',
          'blu': 'blau',
          'verde': 'grün',
          'rosa': 'rosa',
          'bianco': 'weiss',
          'giallo': 'gelb',
          '2 posizioni': '2-fach bestückung',
          'riempimento completo': 'vollbestückung',
          'argento': 'silber',
          'oro': 'gold',
          'rosa chiaro': 'hellrosa',
          'azzurro': 'hellblau',
          'blu scuro': 'dunkelblau',
          'verde scuro': 'dunkelgrün',
          'turchese': 'türkis',
          'violetto': 'violett',
          'viola': 'lila',
          'bacche': 'beere',
          'ovale':'oval',
          'ciliegia': 'kirsche',
          'beige': 'beige',
          'cognac': 'cognac',
          'decoro': 'dekor',
          'menta':'mint',
          'fucsia': 'fuchsia',
          '2 pezzi': '2 stück',
          '12 pezzi': '12 stück',
          'n. 1': 'nr. 1',
          'n. 1.5': 'nr. 1.5',
          'n. 2': 'nr. 2',
          'n. 3': 'nr. 3',
          'n. 10': 'nr. 10',
          'n. 11': 'nr. 11',
          'n. 12': 'nr. 12',
          'n. 13': 'nr. 13',
          'n. 14': 'nr. 14',
          'n. 15': 'nr. 15',
          'n. 16': 'nr. 16',
          'n. 17': 'nr. 17',
          'n. 18': 'nr. 18',
          'n. 19': 'nr. 19',
          'n. 20': 'nr. 20',
          'n. 21': 'nr. 21',
          'n. 22': 'nr. 22',
          'n. 23': 'nr. 23',
          'n. 24': 'nr. 24',
          'n. 25': 'nr. 25',
          'inclinato su entrambi i lati': 'beidseitig schräg',
          'inclinazione unilaterale': 'einseitig schräg',
          'design 18 cm': 'design 18cm',
          'pallone da calcio 18cm': 'fußball 18cm',
          'stagioni 10cm': 'jahreszeiten 10cm',
          'festa della mamma 18cm': 'muttertag 18cm',
          'fiocco di neve 10cm': 'schneeflocke 10cm',
          'inossidabile': 'rostfrei',
          'nichelato': 'vernickelt',
          'plastica': 'kunststoff',
          'duro': 'hart',
          'media': 'mittel',
          'morbido': 'weich',
          'largo': 'breit',
          'stretto': 'schmal',
          'grande': 'groß',
          'piccolo': 'klein',
          'dritto': 'gerade',
          'punta arrotondata': 'gerundete spitze',
          'punta piatta': 'flache spitze',
          'punta sottile': 'schlanke spitze',
          'punta a fiamma': 'flammenspitze',
          'una punta': 'eine spitze',
          'due punte': 'zwei spitzen',
          'appuntito': 'spitz',
          'piegato': 'gebogen',
          'curvato': 'gekröpft',
          'stravagante': 'schräg',
          'misto': 'gemischt',
          'misto (lime abrasive)': 'gemischt (sandblattfeilen)',
          'misto (lime in zaffiro)': 'gemischt (saphirfeilen)',
          'con espositore': 'mit display',
          'senza espositore': 'ohne display',
          "nell'espositore acrilico": 'im acryl-display',
          "nell'espositore acrilico v2": 'im acryl-display v2',
          "nell'espositore in legno": 'im holz-display',
          'colorato': 'bunt',
          'incolore': 'farblos',
          'fiore': 'blume',
          'con cristalli': 'mit kristallen',
          'faggio': 'buche',
          'cellid': 'cellid',
        };

        const locale = this.getCurrentLocale();
        let optionMap = optionMapEN; // default: treat unknown/English locales as needing EN->DE translation

        if (locale.startsWith('de')) {
          optionMap = {}; // German option values already match German alt text directly — no translation needed
        } else if (locale.startsWith('it')) {
          optionMap = optionMapIT;
        }

        let altKey = (variant.option1 || variant.title || '')
          .toLowerCase()
          .trim();

        altKey = optionMap[altKey] || altKey;
        altKey = altKey.toLowerCase();
        altKey = this.normalizeGerman(altKey);

        this.filterByVariant(altKey, variant);
      }

      filterByVariant(altKey, variant) {
        const allSlides = [
          ...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')
        ];

       const matchingSlides = allSlides.filter(slide => {
  const alt = this.normalizeGerman(slide.dataset.mediaAlt);

  return (
    alt === altKey ||
    alt === 'common'
  );
});

        const hasAltMatch = matchingSlides.length > 0;
        const slidesToShow = hasAltMatch ? matchingSlides : allSlides;
        const slideIdsToShow = new Set(slidesToShow.map(s => s.dataset.mediaId));

        this._visibleSlideIds = hasAltMatch ? slideIdsToShow : null;
        this._visibleSlides = slidesToShow;

        // Hide inner content of non-matching slides
        allSlides.forEach(slide => {
          const inner = slide.querySelector('.product__media-container');
          if (!inner) return;
          if (slideIdsToShow.has(slide.dataset.mediaId)) {
            inner.style.visibility = '';
            inner.style.opacity = '';
            slide.style.pointerEvents = '';
          } else {
            inner.style.visibility = 'hidden';
            inner.style.opacity = '0';
            slide.style.pointerEvents = 'none';
          }
        });

        // Hide/show thumbnails
        if (this.elements.thumbnails) {
          const allThumbs = [...this.elements.thumbnails.querySelectorAll('.product__thumbs-item:not(.swiper-slide-duplicate)')];
          allThumbs.forEach((thumb) => {
            const inner = thumb.querySelector('.product__thumbs-container');
            if (!inner) return;
            if (slideIdsToShow.has(thumb.dataset.target)) {
              inner.style.visibility = '';
              inner.style.opacity = '';
              thumb.style.display = '';
            } else {
              inner.style.visibility = 'hidden';
              inner.style.opacity = '0';
              thumb.style.display = 'none';
            }
          });
          if (this.thumbsInstance && this.thumbsInstance.slider) {
            this.thumbsInstance.slider.update();
            this.thumbsInstance.slider.slideTo(0, 0, false);
          }
        }

        // Slide to correct main slide
        if (this.sliderInstance && this.sliderInstance.slider) {
          let targetIndex = allSlides.indexOf(slidesToShow[0]);
          if (variant.featured_media) {
            allSlides.forEach((slide, i) => {
              if (slide.dataset.mediaId == variant.featured_media.id) {
                targetIndex = i;
              }
            });
          }
          this.sliderInstance.slider.slideTo(targetIndex, 0, false);

          // Highlight correct thumb
          const targetMediaId = variant.featured_media
            ? String(variant.featured_media.id)
            : slidesToShow[0]?.dataset.mediaId;
         if (targetMediaId) {
  this._highlightThumb(targetMediaId);
  this._updateNavigationState(targetMediaId);
}
        }

        // Reinit lightbox
        if (this.enableImageZoom) {
          if (this.lightbox) {
            this.lightbox.destroy();
            this.lightbox = null;
          }
          this.initImageZoom(slidesToShow);
        }
      }

      sortMediaItems(variant) {
        if (!variant || !variant.featured_media) return;
        const selectedMedia = this.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
        if (selectedMedia && !FoxTheme.config.mqlMobile) {
          window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
        }
      }
    }
  );
}